import { useState, useEffect, useRef, useCallback } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type NetworkQualityLevel = "excellent" | "good" | "fair" | "poor" | "critical";
export type NetworkTrend = "improving" | "stable" | "degrading";

export interface NetworkMetrics {
  /** Round-trip time in milliseconds */
  latency: number | null;
  /** Mean absolute deviation of RTT samples, in ms */
  jitter: number | null;
  /** Estimated packet loss as a percentage (0–100) */
  packetLoss: number | null;
  /** Downlink speed in Mbps from Navigator Connection API */
  downlink: number | null;
}

export interface NetworkQualityState {
  /** Composite score 0–100 */
  score: number;
  level: NetworkQualityLevel;
  metrics: NetworkMetrics;
  /** Direction the score is heading based on recent history */
  trend: NetworkTrend;
  /** Convenience alias for score, expressed as a percentage */
  streamHealthPercent: number;
  /** Human-readable recommendation when quality degrades */
  recommendation: string | null;
  isOnline: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 3000;
const SCORE_HISTORY_SIZE = 6;   // number of recent scores used for trend detection
const JITTER_WINDOW = 5;        // RTT samples used to compute running jitter
const TREND_THRESHOLD = 8;      // score point change needed to declare a trend
// Using favicon – a 0-byte-ish asset already in every browser cache; the
// response time still includes a full TCP+TLS round-trip on first hit.
const PING_PATH = "/favicon.ico";

// ─── Scoring helpers ──────────────────────────────────────────────────────────

/** Latency (RTT) → sub-score 0–100 */
function latencyScore(ms: number): number {
  if (ms < 50) return 100;
  if (ms < 100) return 80 - ((ms - 50) / 50) * 20;
  if (ms < 150) return 60 - ((ms - 100) / 50) * 20;
  if (ms < 250) return 40 - ((ms - 150) / 100) * 20;
  return Math.max(0, 20 - ((ms - 250) / 500) * 20);
}

/** Jitter → sub-score 0–100 */
function jitterScore(ms: number): number {
  if (ms < 5) return 100;
  if (ms < 10) return 80;
  if (ms < 20) return 60;
  if (ms < 40) return 40;
  return Math.max(0, 20 - (ms - 40) * 0.5);
}

/** Packet loss % → sub-score 0–100 */
function packetLossScore(pct: number): number {
  if (pct === 0) return 100;
  if (pct < 1) return 85;
  if (pct < 2) return 70;
  if (pct < 5) return 50;
  if (pct < 10) return 30;
  return 10;
}

/** Weighted composite score: latency 50 %, jitter 30 %, loss 20 % */
function computeScore(m: NetworkMetrics): number {
  const ls = m.latency != null ? latencyScore(m.latency) : 70;
  const js = m.jitter != null ? jitterScore(m.jitter) : 90;
  const ps = m.packetLoss != null ? packetLossScore(m.packetLoss) : 90;
  return Math.round(ls * 0.5 + js * 0.3 + ps * 0.2);
}

function scoreToLevel(score: number): NetworkQualityLevel {
  if (score >= 80) return "excellent";
  if (score >= 60) return "good";
  if (score >= 40) return "fair";
  if (score >= 20) return "poor";
  return "critical";
}

function getRecommendation(level: NetworkQualityLevel, m: NetworkMetrics): string | null {
  if (level === "excellent" || level === "good") return null;
  if (m.packetLoss != null && m.packetLoss > 5) return "High packet loss detected";
  if (m.latency != null && m.latency > 250) return "High latency – network unstable";
  if (m.jitter != null && m.jitter > 30) return "Connection jitter is high";
  if (level === "critical") return "Critical connection quality";
  return "Network unstable";
}

/**
 * Detect whether quality is improving, stable, or degrading by comparing
 * the average of the last half of the score history to the first half.
 */
function detectTrend(history: number[]): NetworkTrend {
  if (history.length < 4) return "stable";
  const mid = Math.floor(history.length / 2);
  const older = history.slice(0, mid).reduce((a, b) => a + b, 0) / mid;
  const newer = history.slice(mid).reduce((a, b) => a + b, 0) / (history.length - mid);
  const delta = newer - older;
  if (delta > TREND_THRESHOLD) return "improving";
  if (delta < -TREND_THRESHOLD) return "degrading";
  return "stable";
}

// ─── WebRTC stats extraction ──────────────────────────────────────────────────

interface PrevRtpState {
  packetsLost: number;
  packetsSent: number;
  timestamp: number;
}

async function extractWebRTCMetrics(
  pc: RTCPeerConnection,
  prevRtp: React.MutableRefObject<PrevRtpState | null>
): Promise<Partial<NetworkMetrics>> {
  const stats = await pc.getStats();
  let rtt: number | null = null;
  let packetLoss: number | null = null;

  stats.forEach((report) => {
    // Active candidate-pair gives us currentRoundTripTime (seconds → ms)
    if (report.type === "candidate-pair" && (report as any).state === "succeeded") {
      const crt = (report as any).currentRoundTripTime;
      if (crt != null) rtt = crt * 1000;
    }

    // remote-inbound-rtp often has a more stable roundTripTime
    if (report.type === "remote-inbound-rtp") {
      const rt = (report as any).roundTripTime;
      if (rt != null && rtt == null) rtt = rt * 1000;
    }

    // outbound-rtp → delta-based packet loss calculation
    if (report.type === "outbound-rtp" && !(report as any).isRemote) {
      const prev = prevRtp.current;
      const ts: number = (report as any).timestamp ?? 0;
      const lost: number = (report as any).packetsLost ?? 0;
      const sent: number = (report as any).packetsSent ?? 0;

      if (prev && ts > prev.timestamp) {
        const lostDelta = lost - prev.packetsLost;
        const sentDelta = sent - prev.packetsSent;
        const total = sentDelta + Math.max(0, lostDelta);
        if (total > 0) packetLoss = Math.max(0, (lostDelta / total) * 100);
      }

      prevRtp.current = { packetsLost: lost, packetsSent: sent, timestamp: ts };
    }
  });

  return { latency: rtt, packetLoss };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

const INITIAL_STATE: NetworkQualityState = {
  score: 100,
  level: "excellent",
  metrics: { latency: null, jitter: null, packetLoss: null, downlink: null },
  trend: "stable",
  streamHealthPercent: 100,
  recommendation: null,
  isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
};

/**
 * Custom hook that continuously measures network quality and emits a rich
 * quality state every POLL_INTERVAL_MS milliseconds.
 *
 * Measurement sources (in priority order):
 *  1. RTCPeerConnection.getStats() – when the caller passes an active PC.
 *  2. fetch-based HEAD ping – always runs as a latency baseline.
 *  3. navigator.connection – for downlink and supplementary RTT hints.
 *
 * Pass `peerConnection` if the application already maintains a WebRTC session;
 * the hook merges those stats with the ping-based measurement for higher accuracy.
 */
export function useNetworkQuality(peerConnection?: RTCPeerConnection | null): NetworkQualityState {
  const [state, setState] = useState<NetworkQualityState>(INITIAL_STATE);

  // Mutable refs so closures always see latest values without triggering re-renders
  const pingAttempts = useRef(0);
  const pingFailures = useRef(0);
  const prevRttRef = useRef<number | null>(null);
  const jitterSamples = useRef<number[]>([]);
  const scoreHistory = useRef<number[]>([]);
  const prevWebRtcRtp = useRef<PrevRtpState | null>(null);
  const abortCtrl = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Latency probe via HEAD fetch ──────────────────────────────────────────
  const pingFetch = useCallback(async (): Promise<number | null> => {
    // Cancel the previous in-flight probe so we never queue up stale requests
    abortCtrl.current?.abort();
    abortCtrl.current = new AbortController();
    const { signal } = abortCtrl.current;

    pingAttempts.current += 1;
    const t0 = performance.now();

    try {
      await fetch(`${PING_PATH}?_=${pingAttempts.current}`, {
        method: "HEAD",
        cache: "no-store",
        signal,
        // no-cors lets us probe even cross-origin hosts without a preflight
        mode: "no-cors",
      });
      return performance.now() - t0;
    } catch {
      if (!signal.aborted) pingFailures.current += 1;
      return null;
    }
  }, []);

  // ── Running jitter from consecutive RTT samples ───────────────────────────
  const measureJitter = useCallback((rtt: number): number => {
    const prev = prevRttRef.current;
    prevRttRef.current = rtt;
    if (prev == null) return 0;

    jitterSamples.current.push(Math.abs(rtt - prev));
    if (jitterSamples.current.length > JITTER_WINDOW) jitterSamples.current.shift();

    return jitterSamples.current.reduce((a, b) => a + b, 0) / jitterSamples.current.length;
  }, []);

  // ── Main measurement cycle ────────────────────────────────────────────────
  const measure = useCallback(async () => {
    if (!navigator.onLine) {
      setState((prev) => ({
        ...prev,
        isOnline: false,
        score: 0,
        level: "critical",
        streamHealthPercent: 0,
        recommendation: "No internet connection",
      }));
      return;
    }

    const metrics: NetworkMetrics = {
      latency: null,
      jitter: null,
      packetLoss: null,
      downlink: null,
    };

    // 1. WebRTC stats (highest accuracy when a PC is provided)
    if (peerConnection && peerConnection.connectionState !== "closed") {
      try {
        const rtcData = await extractWebRTCMetrics(peerConnection, prevWebRtcRtp);
        if (rtcData.latency != null) metrics.latency = rtcData.latency;
        if (rtcData.packetLoss != null) metrics.packetLoss = rtcData.packetLoss;
      } catch {
        // Silently ignore – we fall back to fetch-based measurement
      }
    }

    // 2. Fetch-based ping (always runs as a latency baseline)
    const pingRtt = await pingFetch();
    if (pingRtt != null) {
      // Blend WebRTC RTT with fetch RTT when both are available
      metrics.latency =
        metrics.latency != null ? (metrics.latency * 0.6 + pingRtt * 0.4) : pingRtt;
      metrics.jitter = measureJitter(pingRtt);
    }

    // 3. Navigator Connection API for downlink and supplementary RTT
    const conn =
      (navigator as any).connection ??
      (navigator as any).mozConnection ??
      (navigator as any).webkitConnection;
    if (conn) {
      if (conn.downlink != null) metrics.downlink = conn.downlink;
      // Use navigator.connection.rtt only as a last resort (coarse 25 ms steps)
      if (conn.rtt != null && metrics.latency == null) metrics.latency = conn.rtt;
    }

    // 4. Packet loss estimation from cumulative ping failures (fallback only)
    if (metrics.packetLoss == null && pingAttempts.current > 0) {
      metrics.packetLoss = (pingFailures.current / pingAttempts.current) * 100;
    }

    // ── Score & derived state ─────────────────────────────────────────────
    const score = computeScore(metrics);
    const level = scoreToLevel(score);
    const recommendation = getRecommendation(level, metrics);

    scoreHistory.current.push(score);
    if (scoreHistory.current.length > SCORE_HISTORY_SIZE) scoreHistory.current.shift();

    const trend = detectTrend(scoreHistory.current);

    setState({
      score,
      level,
      metrics,
      trend,
      streamHealthPercent: score,
      recommendation,
      isOnline: true,
    });
  }, [peerConnection, pingFetch, measureJitter]);

  // ── Online / offline events ───────────────────────────────────────────────
  useEffect(() => {
    const onOnline = () =>
      setState((prev) => ({
        ...prev,
        isOnline: true,
        recommendation: "Connection recovering",
      }));

    const onOffline = () =>
      setState((prev) => ({
        ...prev,
        isOnline: false,
        score: 0,
        level: "critical",
        streamHealthPercent: 0,
        recommendation: "No internet connection",
      }));

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // ── Poll on interval ──────────────────────────────────────────────────────
  useEffect(() => {
    measure(); // fire immediately on mount / when peerConnection changes
    timerRef.current = setInterval(measure, POLL_INTERVAL_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      abortCtrl.current?.abort();
    };
  }, [measure]);

  return state;
}
